/**
 * should be used for fetching data from storyblok
 * using internally in components
 */
import random from 'lodash/random';
import { BannersTrustAllStoryblok } from 'storyblok-types';
import { AlphabeticalItem } from 'organisms/AlphabeticalItemsPage/type';
import { ParseNavigation } from 'modules/stroyblok/services';
import {
  getBannersTrustStories,
  getBrandStories,
  getCategoriesForSearchStories,
  getConfigStories,
} from 'commerce/storyblok/api';

export const getSiteConfig = async (language: string) => {
  const { response, error } = await getConfigStories(language);

  return !error && response?.data
    ? ParseNavigation(response.data.stories as any)
    : null;
};

export const getBrandsBlok = async (
  language: string,
): Promise<AlphabeticalItem[]> => {
  try {
    const { response: firstResponse, error } = await getBrandStories(language, {
      excluding_fields: 'body',
    });

    if (error || !firstResponse?.data?.stories) {
      throw new Error(error?.message || 'No brand stories found');
    }

    const totalBrands = firstResponse.total || 1;
    const lastPage = Math.ceil(totalBrands / 100); // 100 = items per page

    /**
     * Fetch the rest of the brand stories,
     * since the first page only contains 100 items
     * (that's the limit of storyblok)
     */
    const otherStories = await Promise.all(
      Array.from({ length: lastPage - 1 }, (_, i) =>
        getBrandStories(language, { page: i + 2, excluding_fields: 'body' })
          .then((res) => res.response?.data.stories)
          .catch((err) => {
            console.error(`Failed to fetch brands for page ${i + 2}:`, err);
            return [];
          }),
      ),
    );

    const allStories = [firstResponse.data.stories, ...otherStories].flat();

    return allStories as AlphabeticalItem[];
  } catch (error) {
    console.error('An error occurred while fetching brand stories:', error);
    return [];
  }
};

export const getCategoriesForSearch = async (locale: string) => {
  try {
    const { response } = await getCategoriesForSearchStories(locale);
    const categories = response?.data?.stories;

    return categories;
  } catch (error) {
    console.error(error);
  }
};

export const getBannersDocumentsBlok = async (locale: string) => {
  try {
    const { response } = await getBannersTrustStories(locale);

    const { banners } = (response?.data?.story?.content ||
      {}) as BannersTrustAllStoryblok;
    const l = banners?.length || 0;
    const randomIdx = random(0, (l || 1) - 1);

    return {
      banners,
      randomBanners: banners?.sort(() => random(-1, 1)),
      bannerRandom: banners?.[randomIdx],
      pdpBanners: banners?.length
        ? [
            banners?.[randomIdx],
            banners?.[randomIdx === l - 1 ? 0 : randomIdx + 1],
          ]
        : [],
    };
  } catch (error) {
    console.error(error);

    return { banners: undefined, bannerRandom: undefined };
  }
};
